import type { CampaignAbandonment } from "@/lib/types";

/**
 * A campaign's abandon rate in the words the Dialing tab and the Live floor use (platform
 * ADR-206). Shown only: nothing stops a campaign on it.
 *
 * An abandoned call is one a person answered and no rep reached within the campaign's wait. The
 * rule allows 3% of a campaign's live answers over 30 days. A call the machine detection heard a
 * machine on is not counted.
 */

/** A rate as a person reads a limit: "2.5%", "3%", "4.8%". Null is "—", never 0%. */
export function percent(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return `${Number((v * 100).toFixed(1))}%`;
}

/** "1 abandoned of 41 live answers". */
export function countLine(a: { abandoned: number; live_answers: number }): string {
  const answers = a.live_answers === 1 ? "live answer" : "live answers";
  return `${a.abandoned} abandoned of ${a.live_answers} ${answers}`;
}

export type AbandonTone = "warning" | "info" | "ok";

export interface AbandonStatus {
  tone: AbandonTone;
  title: string;
  detail: string;
}

/** What the campaign's rate says, for the line above the meter. */
export function abandonStatus(a: CampaignAbandonment): AbandonStatus {
  const limit = percent(a.limit);
  if (a.over_limit) {
    return {
      tone: "warning",
      title: `Over the ${limit} limit`,
      detail:
        `${countLine(a)} in the last 30 days (${percent(a.abandon_rate)}). The dialer keeps ` +
        "dialing; look at which reps' phones are slow to pick up, or at the wait.",
    };
  }
  if (a.live_answers === 0) {
    return {
      tone: "info",
      title: "No live answers yet",
      detail: "Nobody answered a call of this campaign in the last 30 days, so there is no rate.",
    };
  }
  return { tone: "ok", title: "Under the limit", detail: `The legal limit is ${limit}.` };
}

/** The campaigns the Live floor warns about: over the limit, the highest first. */
export function overLimit(rows: CampaignAbandonment[] | undefined): CampaignAbandonment[] {
  return (rows ?? []).filter((r) => r.over_limit);
}

/** The Live floor's warning, for the campaigns `overLimit` returned. */
export function floorWarning(rows: CampaignAbandonment[]): string | null {
  if (rows.length === 0) return null;
  const named = rows.map((r) => `${r.campaign_name ?? "A campaign"} ${percent(r.abandon_rate)}`);
  const which = rows.length === 1 ? "is" : "are";
  return `${named.join(", ")} ${which} over the ${percent(rows[0]?.limit ?? 0.03)} abandon limit over 30 days.`;
}
